/**
 * PairToAtomCalculator - PA Arrow Type
 *
 * Handles arrows from lone pairs to atoms (particles)
 * This is typically a nucleophilic attack where a lone pair attacks an electrophile
 */

import { v3 } from "@alchemiesolns/maths";
import {
  ArrowCalculator,
  type ArrowCalculationConfig,
  type ArrowEndpoints,
} from "./types";
import {
  defaultLonePairPositioner,
  type LonePairPositioner,
} from "../positioning/LonePairPositioner";
import { calculateParticleDirection, blendDirections } from "../core/geometry";
import { ENDPOINT_CONSTANTS } from "../core/constants";

/**
 * Calculator for Pair-to-Atom (PA) arrows
 *
 * Source: Lone pair
 * Destination: Atom (particle)
 */
export class PairToAtomCalculator extends ArrowCalculator {
  private lpPositioner: LonePairPositioner;

  constructor(lpPositioner: LonePairPositioner = defaultLonePairPositioner) {
    super();
    this.lpPositioner = lpPositioner;
  }

  getTypeName(): string {
    return "Pair→Atom (PA)";
  }

  calculate(config: ArrowCalculationConfig): ArrowEndpoints {
    const { source, destination } = config;

    // Validate types
    if (source.type !== "lonepair") {
      throw new Error(
        `PairToAtomCalculator expects source type 'lonepair', got '${source.type}'`
      );
    }
    if (destination.type !== "particle") {
      throw new Error(
        `PairToAtomCalculator expects destination type 'particle', got '${destination.type}'`
      );
    }

    let endPos = destination.position;
    const atomPos = source.position;

    // Handle fish arrow (shortens to midpoint if not adjacent)
    const fishResult = this.handleFishArrow(config, atomPos, endPos);
    endPos = fishResult.endPos;

    // Step 1: Calculate initial start direction from lone pair
    const initialStartDir = this.lpPositioner.calculateStartDirection(
      source,
      endPos
    );

    // Step 2: Calculate initial start position
    const direction = this.lpPositioner.selectAndAdjustDirection(
      source,
      endPos
    );
    let startPos = this.lpPositioner.calculateStartPoint(source, direction);

    // Step 3: Calculate end direction (particle direction + blend)
    let endDir = calculateParticleDirection(endPos, startPos);
    endDir = v3.normalize(v3.addVectors(endDir, initialStartDir));

    // Apply adjacency adjustment
    if (config.isDestAdjacent) {
      const dirRef = v3.normalize(v3.subVectors(startPos, endPos));
      endDir = v3.normalize(v3.addVectors(endDir, dirRef));
    }

    // Step 4: Blend start and end directions for smoother curve
    const startDir = blendDirections(
      initialStartDir,
      endDir,
      ENDPOINT_CONSTANTS.DIRECTION_BLEND_WEIGHT
    );

    // Recalculate start position with blended direction
    startPos = this.lpPositioner.calculateStartPoint(source, direction);

    // Re-blend end direction
    endDir = blendDirections(
      endDir,
      initialStartDir,
      ENDPOINT_CONSTANTS.DIRECTION_BLEND_WEIGHT
    );

    // Step 4.5 if the startdir and enddir are exact opposite, then angle perpindicular to create a better sloped curve


    // Step 5: Calculate final endpoint
    const p3 = v3.addScaledVector(
      endPos,
      endDir,
      config.isDestAdjacent
        ? fishResult.destOffset
        : ENDPOINT_CONSTANTS.PARTICLE_GENERIC_OFFSET
    );

    return {
      startPos,
      endPos,
      p3,
      startDir,
      endDir,
      isDestAdjacent: config.isDestAdjacent,
    };
  }
}

/**
 * Default singleton instance
 */
export const defaultPairToAtomCalculator = new PairToAtomCalculator();
